import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Submission } from '_api/types';
import { converToDayjs } from '_constants';
import { StyledDetailDescription, StyledFormLabel } from 'components/atoms';
import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo, useState } from 'react';

import { ActivitySubmissionProps } from '../types';

export const ActivitySubmission = ({ data }: ActivitySubmissionProps) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    converToDayjs(dayjs().startOf('month').format('MM-DD-YYYY')),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    converToDayjs(dayjs().endOf('month').format('MM-DD-YYYY')),
  );

  const filteredNotes = useMemo(() => {
    if (!startDate || !endDate) {
      return data;
    }
    return data.filter((note: Submission) => {
      const noteDate = dayjs(note.date);
      return noteDate.isAfter(startDate) && noteDate.isBefore(endDate.add(1, 'day'));
    });
  }, [data, startDate, endDate]);

  const statusMessage = (evaluation: number) => {
    let message = '';
    switch (evaluation) {
      case 0:
        message = 'It was too hard';
        break;
      case 1:
        message = 'Frustrating';
        break;
      case 2:
        message = 'Discouraging';
        break;
      case 3:
        message = 'Neutral';
        break;
      case 4:
        message = 'Okay';
        break;
      case 5:
        message = 'Good';
        break;
      case 6:
        message = 'Great';
        break;
      case 7:
        message = 'Awesome';
        break;
      case 8:
        message = 'Too Easy';
    }
    return message;
  };

  const selectEmojiUnicode = (evaluation: number) => {
    let emoji = '';
    switch (evaluation) {
      case 0:
        emoji = String.fromCodePoint(0x1f44e);
        break;
      case 1:
        emoji = String.fromCodePoint(0x1f616);
        break;
      case 2:
        emoji = String.fromCodePoint(0x1f627);
        break;
      case 3:
        emoji = String.fromCodePoint(0x1f636);
        break;
      case 4:
        emoji = String.fromCodePoint(0x1f44d);
        break;
      case 5:
        emoji = String.fromCodePoint(0x1f603);
        break;
      case 6:
        emoji = String.fromCodePoint(0x1f929);
        break;
      case 7:
        emoji = String.fromCodePoint(0x1f973);
        break;
      case 8:
        emoji = String.fromCodePoint(0x0001f971);
        break;
    }
    return emoji;
  };

  return (
    <Box>
      <StyledDetailDescription sx={{ marginTop: 10 }}>
        Select Date Range For Notes
      </StyledDetailDescription>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 20,
          marginTop: 10,
          width: '50%',
        }}
      >
        <DatePicker label="Start Date" value={startDate} onChange={setStartDate} />
        <DatePicker label="End Date" value={endDate} onChange={setEndDate} />
      </Box>
      {filteredNotes.map((data: Submission, index: number) => (
        <Box
          key={`activity-submission-${data.id}`}
          sx={{
            borderBottom: index !== data.notes.length - 1 ? '1px solid #ccc' : 'none',
            paddingBottom: '16px',
            marginBottom: '16px',
          }}
        >
          <StyledFormLabel sx={{ marginTop: '8px' }}>
            {dayjs(data.date).format('MM/DD/YYYY')}
          </StyledFormLabel>
          <StyledFormLabel>STATUS</StyledFormLabel>
          <Typography sx={{ marginBottom: '8px', marginLeft: '0.25rem' }}>
            {statusMessage(data.evaluation) + ' ' + selectEmojiUnicode(data.evaluation)}
          </Typography>
          <StyledFormLabel sx={{ marginTop: '8px' }}>NOTES</StyledFormLabel>
          <Typography sx={{ marginLeft: '0.25rem' }}>{data.notes}</Typography>
        </Box>
      ))}
    </Box>
  );
};
